/* ============================================================================
  TOGGLE SYSTEM USAGE GUIDE
  ----------------------------------------------------------------------------
  This system controls visibility using Bootstrap's `d-none` class.

  It works off data attributes placed on the CONTROL element
  (checkbox, radio, select, button, etc).

  The system supports five modes:
    - on
    - off
    - toggle
    - swap
    - checked

  ----------------------------------------------------------------------------
  BASIC CONCEPT

  The CONTROL element declares:
    data-toggle-target="#selector"
    data-toggle-state="on|off|toggle|swap|checked"

  When the control changes:
    → Target elements have `d-none` added or removed.

  Any required fields inside hidden containers:
    → Have `required` removed automatically.
    → Are restored when shown.

  ----------------------------------------------------------------------------
  TARGETING

  Targets must be valid query selectors:
    data-toggle-target="#my_div"
    data-toggle-target=".my_class"
    data-toggle-target="#id1, #id2"

  Multiple selectors are allowed (comma separated).

  ----------------------------------------------------------------------------
  STATES

  1. data-toggle-state="on"
     → Always SHOW the target(s).
     → Removes `d-none`.

  2. data-toggle-state="off"
     → Always HIDE the target(s).
     → Adds `d-none`.

  3. data-toggle-state="toggle"
     → Flips visibility each time.
     → If visible → hides
     → If hidden → shows

  4. data-toggle-state="swap"
     → Uses:
         data-toggle-show="#selector"
         data-toggle-hide="#selector"
     → Shows one group and hides another.

  5. data-toggle-state="checked"
     → Visibility follows checkbox/radio checked state.
     → If checked → show
     → If unchecked → hide

     Use when:
       - Checkbox should control visibility directly
       - Radio button should control a container directly

  ----------------------------------------------------------------------------
  INITIAL STATE ON PAGE LOAD

  On turbo:load:
    If a control is already :checked,
    the toggle logic is automatically applied.

  ----------------------------------------------------------------------------
============================================================================ */

document.addEventListener("turbo:load", () => {
  const controls = document.querySelectorAll("[data-toggle-target], [data-toggle-show], [data-toggle-hide]");

  const setVisibility = (targets, visible) => {
    targets.forEach(el => {
      el.classList.toggle("d-none", !visible);

      const fields = el.querySelectorAll("input, select, textarea");

      fields.forEach(field => {
        if (visible) {
          if (field.dataset.wasRequired === "true") {
            field.required = true;
          }
        } else {
          if (field.required) {
            field.dataset.wasRequired = "true";
          }
          field.required = false;
        }
      });
    });
  };

  const applySwap = (control) => {
    const showTargets = control.dataset.toggleShow
      ? Array.from(document.querySelectorAll(control.dataset.toggleShow))
      : [];

    const hideTargets = control.dataset.toggleHide
      ? Array.from(document.querySelectorAll(control.dataset.toggleHide))
      : [];

    setVisibility(showTargets, true);
    setVisibility(hideTargets, false);
  };

  controls.forEach(control => {
    const mode = (control.dataset.toggleState || "").toLowerCase();
    const eventType = control.tagName.toLowerCase() === "button" ? "click" : "change";

    control.addEventListener(eventType, (e) => {
      if (eventType === "click") e.preventDefault();

      const targets = control.dataset.toggleTarget
        ? Array.from(document.querySelectorAll(control.dataset.toggleTarget))
        : [];

      switch (mode) {
        case "on":
          setVisibility(targets, true);
          break;

        case "off":
          setVisibility(targets, false);
          break;

        case "toggle":
          targets.forEach(el => el.classList.toggle("d-none"));

          const icon = control.querySelector("i");
          const iconA = control.dataset.toggleIconA;
          const iconB = control.dataset.toggleIconB;

          if (icon && iconA && iconB) {
            icon.classList.toggle(iconA);
            icon.classList.toggle(iconB);
          }
          break;

        case "swap":
          applySwap(control);
          break;

        case "checked":
          setVisibility(targets, control.matches(":checked"));
          break;
      }
    });
  });

  controls.forEach(control => {
    if (!control.matches(":checked")) return;

    const mode = (control.dataset.toggleState || "").toLowerCase();

    const targets = control.dataset.toggleTarget
      ? Array.from(document.querySelectorAll(control.dataset.toggleTarget))
      : [];

    switch (mode) {
      case "on":
        setVisibility(targets, true);
        break;

      case "off":
        setVisibility(targets, false);
        break;

      case "toggle":
        targets.forEach(el => el.classList.toggle("d-none"));
        break;

      case "swap":
        applySwap(control);
        break;

      case "checked":
        setVisibility(targets, true);
        break;
    }
  });
});